import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
  Box,
  Alert,
  Autocomplete,
} from "@mui/material";
import { saveSalesTargetAPI, fetchSalesPersonsAPI } from "../../config";
import { apiData } from "../../services/api_service";

const monthOptions = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const currentYear = new Date().getFullYear();
const yearOptions = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

const SalesTargetModal = ({ open, onClose, onSuccess, initialData = null, salesPersons = [] }) => {
  const [formData, setFormData] = useState({
    sales_person_id: "",
    year: currentYear,
    month: new Date().getMonth() + 1,
    target_amount: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [spOptions, setSpOptions] = useState([]);

  // Fetch salespersons directly from API if prop is empty
  const fetchSalesPersons = async () => {
    try {
      const res = await apiData("get", fetchSalesPersonsAPI);
      const list = res?.data?.data || res?.data || [];
      if (Array.isArray(list)) {
        setSpOptions(list);
      }
    } catch (err) {
      console.error("Error fetching sales persons for modal:", err);
    }
  };

  useEffect(() => {
    if (salesPersons && salesPersons.length > 0) {
      setSpOptions(salesPersons);
    } else if (open) {
      fetchSalesPersons();
    }
  }, [salesPersons, open]);

  useEffect(() => {
    setErrorMsg("");
    setSuccessMsg("");
    if (initialData) {
      setFormData({
        sales_person_id: initialData.sales_person_id || "",
        year: initialData.year || currentYear,
        month: initialData.month || new Date().getMonth() + 1,
        target_amount: initialData.target_amount || "",
        notes: initialData.notes || "",
      });
    } else {
      setFormData({
        sales_person_id: "",
        year: currentYear,
        month: new Date().getMonth() + 1,
        target_amount: 0,
        notes: "",
      });
    }
  }, [initialData, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!formData.sales_person_id) {
      setErrorMsg("Please select a sales person");
      return;
    }
    if (!formData.target_amount || parseFloat(formData.target_amount) < 0) {
      setErrorMsg("Please enter a valid target amount");
      return;
    }

    try {
      setLoading(true);
      const res = await apiData("post", saveSalesTargetAPI, {
        sales_person_id: formData.sales_person_id,
        year: parseInt(formData.year, 10),
        month: parseInt(formData.month, 10),
        target_amount: parseFloat(formData.target_amount),
        notes: formData.notes,
      });

      if (res?.success || res?.statusCode === 200) {
        setSuccessMsg(res?.msg || "Sales target saved successfully!");
        setTimeout(() => {
          onSuccess && onSuccess();
          onClose();
        }, 600);
      } else {
        setErrorMsg(res?.msg || "Failed to save sales target");
      }
    } catch (err) {
      console.error("Save Sales Target Error:", err);
      setErrorMsg(err?.response?.data?.message || "An error occurred while saving target");
    } finally {
      setLoading(false);
    }
  };

  const selectedSalesPerson = spOptions.find(
    (sp) => Number(sp.sales_person_id || sp.ID || sp.id) === Number(formData.sales_person_id)
  ) || null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, bgcolor: "#1976d2", color: "#fff" }}>
        {initialData ? "Edit Sales Target" : "Set New Sales Target"}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ p: 3 }}>
          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMsg}
            </Alert>
          )}
          {successMsg && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMsg}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Year"
                name="year"
                value={formData.year}
                onChange={handleChange}
                required
                size="small"
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Month"
                name="month"
                value={formData.month}
                onChange={handleChange}
                required
                size="small"
              >
                {monthOptions.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                size="small"
                options={spOptions}
                disabled={Boolean(initialData?.sales_person_id)}
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option;
                  const name = option.employee_name || option.name || "";
                  const code = option.employee_no ? ` (${option.employee_no})` : "";
                  return `${name}${code}`.trim();
                }}
                value={selectedSalesPerson}
                onChange={(event, newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    sales_person_id: newValue ? newValue.sales_person_id || newValue.ID || newValue.id : "",
                  }));
                }}
                isOptionEqualToValue={(option, val) =>
                  Number(option.sales_person_id || option.ID || option.id) === Number(val.sales_person_id || val.ID || val.id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sales Person"
                    placeholder="Search sales representative..."
                    size="small"
                    required={!formData.sales_person_id}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Target Amount (AED)"
                name="target_amount"
                type="number"
                value={formData.target_amount}
                onChange={handleChange}
                required
                size="small"
                inputProps={{ min: 0, step: "100" }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes / Remarks"
                name="notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={handleChange}
                size="small"
                placeholder="Optional comments regarding target setup"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={loading} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading} color="primary">
            {loading ? "Saving..." : "Save Target"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SalesTargetModal;
